import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, Typography } from '@mui/material';
import { useActivePriceList } from '../hooks/useActivePriceList';

interface PriceListDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 🎯 단가표 다이얼로그 컴포넌트
 * 
 * 재사용 가능한 단가표 표시 다이얼로그
 * - 이미지 여백 최소화로 내용을 명확하게 표시
 * - 모바일에서는 전체화면, 데스크탑에서는 95% 화면 활용
 * - 이미지 클릭으로 확대/축소 토글 기능
 * - 오버레이 닫기 버튼으로 공간 절약
 */
const PriceListDialog = ({ open, onClose }: PriceListDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { url, loading, error } = useActivePriceList();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxWidth: isMobile ? '100vw' : '95vw',
          maxHeight: isMobile ? '100vh' : '95vh',
          margin: isMobile ? 0 : 2,
        }
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* 닫기 버튼 오버레이 - 스크롤해도 고정 위치 */}
        <Box
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            borderRadius: '50%',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              transform: 'scale(1.05)',
            }
          }}
        >
          <IconButton 
            onClick={onClose}
            sx={{ 
              color: 'white',
              width: 44,
              height: 44,
              '&:hover': { 
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>
        
        {/* 이미지 컨테이너 */}
        <DialogContent 
          sx={{ 
            p: 0, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'flex-start',
            height: '100%',
            overflow: 'auto', // 스크롤 가능하도록 변경
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }
            }
          }}
        >
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography>단가표를 불러오는 중…</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ p: 4 }}>
              <Typography color="error">단가표를 불러오지 못했습니다.</Typography>
            </Box>
          ) : (
            <img
              src={url || '/assets/price-list2.png'}
              alt="단가표 이미지"
              style={{
                width: '100%',
                height: 'auto',
                minHeight: '100%',
                objectFit: 'contain',
                maxWidth: 'none',
              }}
              onClick={(e) => {
                const img = e.target as HTMLImageElement;
                if (img.style.objectFit === 'contain') {
                  img.style.objectFit = 'cover';
                  img.style.cursor = 'zoom-out';
                } else {
                  img.style.objectFit = 'contain';
                  img.style.cursor = 'zoom-in';
                }
              }}
              onLoad={(e) => {
                (e.target as HTMLImageElement).style.cursor = 'zoom-in';
              }}
              onError={(e) => {
                // 스토리지 URL 접근 실패 시 정적 이미지로 폴백
                (e.target as HTMLImageElement).src = '/assets/price-list2.png';
              }}
            />
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default PriceListDialog;